import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  BRAND,
  LANG,
  LOCALE,
  DEFAULT_OG_IMAGE,
  TWITTER_CARD,
  absoluteUrl,
  assetUrl,
} from "./siteConfig";
import { getRouteSeo, isIndexable, normalisePath } from "./routeSeo";

/**
 * The single SEO component for the whole site.
 *
 * On a static route it needs no props — title, description and indexability
 * are resolved from routes.seo.json by the current pathname. Dynamic pages
 * (dish, category, admin-managed landing pages) pass their own values derived
 * from fetched data.
 *
 * Everything it emits is also what the prerenderer captures, because the
 * prerenderer snapshots the DOM after Helmet has applied these tags. There is
 * no second, server-side copy of this metadata to keep in sync.
 *
 * @param title        Full <title>. Falls back to the registry, then the brand.
 * @param description  Meta description. Falls back to the registry.
 * @param path         Canonical path. Defaults to the current location.
 * @param image        OG/Twitter image (absolute URL or /public path).
 * @param index        Overrides the registry's indexability decision.
 * @param type         og:type. "website" for listings, "article" for content.
 * @param jsonLd       One schema object, or an array of them. Falsy entries dropped.
 */
export default function Seo({
  title,
  description,
  path,
  image,
  index,
  type = "website",
  jsonLd,
  children,
}) {
  const location = useLocation();
  const pathname = normalisePath(path || location.pathname);

  const registry = getRouteSeo(pathname);

  const resolvedTitle = title || registry?.title || BRAND;
  const resolvedDescription = description || registry?.description || "";
  const canonical = absoluteUrl(pathname);
  const resolvedImage = image ? assetUrl(image) : DEFAULT_OG_IMAGE;

  const shouldIndex = typeof index === "boolean" ? index : isIndexable(pathname);
  const robots = shouldIndex
    ? "index, follow, max-image-preview:large, max-snippet:-1"
    : "noindex, nofollow";

  const schemas = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);

  return (
    <Helmet htmlAttributes={{ lang: LANG }} prioritizeSeoTags>
      <title>{resolvedTitle}</title>
      {resolvedDescription && (
        <meta name="description" content={resolvedDescription} />
      )}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robots} />

      {/* Open Graph */}
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:locale" content={LOCALE} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={resolvedTitle} />
      {resolvedDescription && (
        <meta property="og:description" content={resolvedDescription} />
      )}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={resolvedImage} />

      {/* Twitter */}
      <meta name="twitter:card" content={TWITTER_CARD} />
      <meta name="twitter:title" content={resolvedTitle} />
      {resolvedDescription && (
        <meta name="twitter:description" content={resolvedDescription} />
      )}
      <meta name="twitter:image" content={resolvedImage} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {children}
    </Helmet>
  );
}
